'use client';

import { ReactNode, useEffect, useState } from 'react';
import { ClearFilter, useCountSelectedFilters } from './useSelectedFilters';
import { inline } from '@/uiPrimitives/utility/display.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { SideOverlay } from '@/uiComposites/layout/SideOverlay';
import { stack } from '@/uiPrimitives/layout/stack';
import { gridStack } from '@/uiPrimitives/layout/gridStack';
import { box } from '@/uiPrimitives/layout/box';
import { icon } from '@/uiPrimitives/media/icon';
import { closeIcon } from '@/uiIcons/closeIcon.css';
import { position } from '@/uiPrimitives/layout/position';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { focusBox } from '@/uiPrimitives/base/focus.css';
import { textBlock } from '@/uiPrimitives/typography/text';
import { secondaryButton } from '@/uiComposites/interactive/callToAction';
import { mq640px } from '@/uiPrimitives/base/mediaQuery.definitions.css';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { pile } from '@/uiPrimitives/layout/pile';
import { useIsNavigating } from '@/globals/routing/DOPRouter';
import { translateInline } from '@/globals/translate/translateInline';

export const FilterOverlayClient = ({
	children,
	basePathname,
	totalResultsElement,
	className,
}: {
	children: ReactNode;
	basePathname: string;
	totalResultsElement?: ReactNode;
	className?: string;
}) => {
	const [opened, setOpened] = useState(false);
	const [visible, setVisible] = useState(false);
	const selectedFilterCount = useCountSelectedFilters();
	const loading = useIsNavigating();

	useEffect(() => {
		const mql = matchMedia(mq640px.max);
		const abortController = new AbortController();
		setVisible(mql.matches);
		mql.addEventListener(
			'change',
			(event) => {
				setVisible(event.matches);
			},
			{ signal: abortController.signal },
		);
		return () => {
			abortController.abort();
		};
	}, []);

	return (
		<div className={classes(className)}>
			<button
				onClick={() => {
					setOpened(true);
				}}
				className={classes(secondaryButton({}))}
				{...addTestSelector('uiOpenFilterOverlayButton')}
			>
				{translateInline({ en: 'Filters', nl: 'Filters' })}{' '}
				{selectedFilterCount > 0 && `(${selectedFilterCount})`}
			</button>
			{
				<SideOverlay
					open={opened && visible}
					onClose={() => {
						if (visible) setOpened(false);
					}}
					onCancel={() => {
						if (visible) setOpened(false);
					}}
					classNameInner={classes(gridStack({}))}
					{...addTestSelector('uiFilterOverlay')}
				>
					<div
						className={classes(
							gridStack({
								gap: '5 | betweenParagraphs',
								gridTemplateRows: 'auto minmax(0, 1fr) auto',
							}),
						)}
					>
						<button
							onClick={() => {
								setOpened(false);
							}}
							className={classes(
								box({
									backgroundColor: 'positiveBlue',
									inlineSize: '3 | h1',
									blockSize: '3 | h1',
								}),
								pile({ alignItems: 'center', justifyItems: 'center' }),
								position({ position: 'sticky', insetBlockStart: '0' }),
								focusBox,
							)}
							aria-label={translateInline({
								en: 'Close',
								nl: 'Sluiten',
							})}
						>
							<i
								className={classes(
									icon({
										icon: closeIcon,
										inlineSize: '-1 | small',
										blockSize: '-1 | small',
									}),
								)}
							/>
						</button>
						<div className={classes(box({ paddingInline: indentSize }))}>
							{children}
						</div>
						<div
							className={classes(
								box({
									backgroundColor: 'negativeBlue',
									paddingInline: indentSize,
									paddingBlock: '3 | h1',
								}),
								stack({
									gap: '3 | h1',
								}),
								position({ position: 'sticky', insetBlockEnd: '0' }),
							)}
						>
							{selectedFilterCount > 0 && (
								<ClearFilter
									basePathname={basePathname}
									className={classes(
										textBlock({
											color: 'positiveBlue',
											textDecorationLine: 'underline',
											capSize: '-1 | small',
										}),
									)}
									{...addTestSelector('uiOverlayClearAllFiltersButton')}
								>
									{translateInline({
										en: 'Clear all filters',
										nl: 'Alle filters wissen',
									})}
								</ClearFilter>
							)}
							<button
								onClick={() => {
									setOpened(false);
								}}
								className={classes(
									box({
										backgroundColor: 'positiveBlue',
										paddingInline: indentSize,
										paddingBlock: '2 | h2',
									}),
									textBlock({
										capSize: '-1 | small',
										fontWeight: 'bold',
										textAlign: 'center',
									}),
								)}
								{...addTestSelector('uiShowResultsButton')}
							>
								{translateInline({
									en: 'View results',
									nl: 'Bekijk resultaten',
								})}{' '}
								<span
									className={classes(
										box({ opacity: loading ? '0' : '1' }),
										inline,
									)}
								>
									{totalResultsElement}
								</span>
							</button>
						</div>
					</div>
				</SideOverlay>
			}
		</div>
	);
};
